import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import FormModal from '@shared/ui/FormModal';
import WorkspacePickerList from './workspace/WorkspacePickerList';

interface Workspace {
  id: string;
  name: string;
  description: string;
  owner_org_name: string;
}

interface BulkWorkspaceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'bill' | 'legislator' | 'candidate';
  entityCount: number;
  actionType: 'add' | 'remove';
  onSave: (workspaceIds: string[]) => Promise<void>;
}

const ENTITY_LABELS: Record<string, { singular: string; plural: string }> = {
  bill: { singular: 'bill', plural: 'bills' },
  legislator: { singular: 'legislator', plural: 'legislators' },
  candidate: { singular: 'candidate', plural: 'candidates' },
};

const BulkWorkspaceSelectionModal: React.FC<BulkWorkspaceSelectionModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityCount,
  actionType,
  onSave,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const labels = ENTITY_LABELS[entityType] ?? { singular: entityType, plural: `${entityType}s` };
  const entityLabel = entityCount === 1 ? labels.singular : labels.plural;

  useEffect(() => {
    if (isOpen) {
      fetchWorkspaces();
      setSelectedWorkspaces([]);
    }
  }, [isOpen]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workspaces');
      setWorkspaces(response.data || []);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaces(prev =>
      prev.includes(workspaceId)
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleSave = async () => {
    if (selectedWorkspaces.length === 0) {
      alert('Please select at least one workspace.');
      return;
    }

    try {
      setSaving(true);
      await onSave(selectedWorkspaces);
      onClose();
    } catch (error) {
      console.error(`Error with bulk ${actionType}:`, error);
      alert(`Failed to ${actionType === 'add' ? 'add' : 'remove'} ${entityLabel}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedWorkspaces([]);
    onClose();
  };

  const noun = labels.plural.charAt(0).toUpperCase() + labels.plural.slice(1);

  return (
    <FormModal
      isOpen={isOpen}
      title={actionType === 'add' ? `Add ${noun.toLowerCase()} to workspaces` : `Remove ${noun.toLowerCase()} from workspaces`}
      description={`${entityCount} ${entityLabel} selected`}
      onClose={handleClose}
      onSubmit={handleSave}
      size="lg"
      submitLabel={actionType === 'add' ? 'Add to workspaces' : 'Remove from workspaces'}
      submitVariant={actionType === 'remove' ? 'danger' : 'primary'}
      isSubmitting={saving}
      submitDisabled={selectedWorkspaces.length === 0}
      footerLeft={
        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {selectedWorkspaces.length} workspace{selectedWorkspaces.length !== 1 ? 's' : ''} selected
        </span>
      }
    >
      <WorkspacePickerList
        workspaces={workspaces}
        selectedIds={selectedWorkspaces}
        onToggle={handleWorkspaceToggle}
        loading={loading}
        prompt={`Select workspaces to ${actionType === 'add' ? `add ${entityLabel} to` : `remove ${entityLabel} from`}:`}
      />
    </FormModal>
  );
};

export default BulkWorkspaceSelectionModal;
